import { AlertTriangle, UserRoundCheck, UserRoundX, X as CloseIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import type { AttendanceStatus, ChildWithAttendance } from "nautika-types";
import { useEffect } from "react";

export function ChildAttendanceCard({
  child,
  onAttendanceChange,
  isUpdating = false,
  isNew = false,
  canRemove = false,
  onRemove,
  onCardClick,
  readOnly = false,
}: {
  child: ChildWithAttendance;
  onAttendanceChange: (childId: string, status: AttendanceStatus) => void;
  isUpdating?: boolean;
  isNew?: boolean;
  canRemove?: boolean;
  onRemove?: () => void;
  onCardClick?: (child: ChildWithAttendance) => void;
  readOnly?: boolean;
}) {
  const isPresent = child.attendance === "present";
  const isAbsent = child.attendance === "absent";

  useEffect(() => {
    console.log(isUpdating ? "TRUE" : "FALSE");
  }, [isUpdating]);

  return (
    <div
      onClick={() => onCardClick?.(child)}
      className={cn(
        // min-h to align with AddChildCombobox trigger (min-h-[64px]) so that states A (today), B (future readOnly) and C (add) share identical vertical height
        "relative flex min-h-[64px] items-center justify-between rounded-lg border px-4 py-4 transition-colors",
        !isPresent && !isAbsent && "bg-white",
        isPresent && "border-green-200 bg-green-100/60",
        isAbsent && "border-red-200 bg-red-100/60",
        isNew && "ring-1 ring-blue-300/60 animate-in fade-in-0 zoom-in-95",
        onCardClick && "cursor-pointer hover:bg-muted/40"
      )}
    >
      {canRemove && (
        <Tooltip delay={250}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.();
              }}
              aria-label="Supprimer cet enfant"
              className={cn(
                "group absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white text-white shadow-md transition",
                "group hover:cursor-pointer hover:bg-red-600 focus:ring-2 focus:ring-red-400 focus:ring-offset-1 focus:outline-none"
              )}
            >
              <CloseIcon strokeWidth={3} className="h-3.5 w-3.5 text-red-600 transition group-hover:text-white group-active:scale-90" />
            </button>
          </TooltipTrigger>
          <TooltipContent sideOffset={4} className="px-2 py-1 text-[10px] leading-tight">
            Supprimer cet enfant
          </TooltipContent>
        </Tooltip>
      )}
      {child.notes ? (
        <Tooltip delay={200}>
          <div className="flex min-w-0 flex-1 items-center text-sm font-medium text-gray-900">
            <TooltipTrigger asChild>
              <div className="flex min-w-0 cursor-help items-center gap-1">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" />
                <span className="truncate">
                  {child.firstName} {child.lastName}
                </span>
              </div>
            </TooltipTrigger>
            <span className="ml-2 flex-shrink-0 font-light whitespace-nowrap text-muted-foreground">{child.age} ans</span>
          </div>
          <TooltipContent side="top" className="max-w-xs text-[11px] leading-snug break-words">
            {child.notes}
          </TooltipContent>
        </Tooltip>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-1 text-sm font-medium text-gray-900">
          <span className="truncate">
            {child.firstName} {child.lastName}
          </span>
          <span className="mx-2 flex-shrink-0 font-light whitespace-nowrap text-muted-foreground">{child.age} ans</span>
        </div>
      )}
      {!readOnly && (
        <div className="flex flex-shrink-0 items-center gap-3">
          <button
            type="button"
            aria-label={isPresent ? "Marquer en attente" : "Marquer présent"}
            onClick={(e) => {
              e.stopPropagation();
              onAttendanceChange(child.id, isPresent ? "pending" : "present");
            }}
            disabled={isUpdating}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-full transition focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:outline-none",
              isPresent ? "bg-green-500 text-white hover:brightness-115" : "bg-green-100 text-green-700 hover:brightness-105",
              isUpdating ? "cursor-progress opacity-70" : "cursor-pointer"
            )}
          >
            <UserRoundCheck className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label={isAbsent ? "Marquer en attente" : "Marquer absent"}
            onClick={(e) => {
              e.stopPropagation();
              onAttendanceChange(child.id, isAbsent ? "pending" : "absent");
            }}
            disabled={isUpdating}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-full transition focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:outline-none",
              isAbsent ? "bg-red-500 text-white hover:brightness-115" : "bg-red-100 text-red-700 hover:brightness-105",
              isUpdating ? "cursor-progress opacity-70" : "cursor-pointer"
            )}
          >
            <UserRoundX className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
